// components/CodigoActivacion.jsx
// Componente para validar código de activación rascable

import React, { useState, useRef, useEffect } from 'react';

/**
 * CodigoActivacion - Pantalla para ingresar el código rascable de 6 caracteres
 * 
 * Props:
 * - codigoAcceso: string - El código del QR/NFC
 * - onValidado: function(tokenValidacion) - Callback cuando el código es válido
 * - onError: function(error) - Callback para errores
 * - intentosRestantes: number - Intentos disponibles (default 7)
 */
const CodigoActivacion = ({ 
  objetoId, 
  onValidado, 
  onError,
  intentosRestantes: initialIntentos = 7 
}) => {
  const [codigo, setCodigo] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [intentosRestantes, setIntentosRestantes] = useState(initialIntentos);
  const [bloqueado, setBloqueado] = useState(false);
  
  const inputRefs = useRef([]);
  
  // Auto-focus en el primer input
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);
  
  // Manejar cambio en cada input
  const handleChange = (index, value) => {
    // Solo alfanuméricos
    const char = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(-1);
    
    const newCodigo = [...codigo];
    newCodigo[index] = char;
    setCodigo(newCodigo);
    setError('');
    
    // Auto-avanzar al siguiente input
    if (char && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit cuando se completan los 6 caracteres
    if (char && index === 5) {
      const codigoCompleto = newCodigo.join('');
      if (codigoCompleto.length === 6) {
        handleSubmit(codigoCompleto);
      }
    }
  };
  
  // Manejar tecla backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codigo[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };
  
  // Manejar paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').toUpperCase().replace(/[^A-Za-z0-9]/g, '').slice(0, 6);
    
    if (pastedData.length > 0) {
      const newCodigo = [...codigo];
      for (let i = 0; i < 6; i++) {
        newCodigo[i] = pastedData[i] || '';
      }
      setCodigo(newCodigo);
      
      // Focus en el último carácter pegado o el siguiente vacío
      const nextEmpty = newCodigo.findIndex(c => !c);
      if (nextEmpty === -1 && pastedData.length === 6) {
        // Código completo, auto-submit
        handleSubmit(pastedData);
      } else if (nextEmpty !== -1) {
        inputRefs.current[nextEmpty]?.focus();
      }
    }
  };
  
  // Validar código
  const handleSubmit = async (codigoCompleto = null) => {
    const codigoFinal = codigoCompleto || codigo.join('');
    
    if (codigoFinal.length !== 6) {
      setError('Ingresa los 6 caracteres del código');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch('/api/objetos/validar-codigo-activacion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          objetoId,
          codigoActivacion: codigoFinal
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Código válido
        onValidado(data.tokenValidacion);
      } else if (response.status === 401) {
        // Código incorrecto
        setIntentosRestantes(data.intentosRestantes || intentosRestantes - 1);
        setError(data.error || 'Código incorrecto');
        setCodigo(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      } else if (response.status === 429 || data.bloqueado) {
        // Bloqueado
        setBloqueado(true);
        setError(data.error || 'Tarjeta bloqueada');
        if (onError) onError({ bloqueado: true, codigoSoporte: data.codigoSoporte });
      } else {
        setError(data.error || 'Error al validar el código');
      }
    } catch (err) {
      console.error('Error validando código:', err);
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };
  
  if (bloqueado) {
    return (
      <div style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          maxWidth: '400px',
          width: '100%',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          padding: '40px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#FEE2E2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            fontSize: '40px'
          }}>
            🔒
          </div>
          
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '600', 
            color: '#1F2937',
            marginBottom: '12px'
          }}>
            Tarjeta Bloqueada
          </h2>
          
          <p style={{ 
            color: '#6B7280', 
            marginBottom: '24px',
            lineHeight: '1.6'
          }}>
            Se han realizado demasiados intentos incorrectos. 
            Por seguridad, esta tarjeta ha sido bloqueada.
          </p>
          
          <div style={{
            backgroundColor: '#F3F4F6',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '24px'
          }}>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
              Contacta a soporte con este código:
            </p>
            <p style={{ 
              fontSize: '20px', 
              fontWeight: '700', 
              color: '#1F2937',
              fontFamily: 'monospace',
              letterSpacing: '2px'
            }}>
              {error.includes('BLQ-') ? error.split('BLQ-')[1]?.split('.')[0] : 'SOPORTE'}
            </p>
          </div>
          
          <a 
            href="/soporte" 
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              backgroundColor: '#FBBF24',
              color: '#1F2937',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '600'
            }}
          >
            Contactar Soporte
          </a>
        </div>
      </div>
    );
  }
  
  return (
    <div style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '450px',
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
        padding: '40px'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#FEF3C7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: '40px'
          }}>
            🎫
          </div>
          
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '600', 
            color: '#1F2937',
            marginBottom: '8px'
          }}>
            Código de Activación
          </h2>
          
          <p style={{ 
            color: '#6B7280', 
            fontSize: '15px',
            lineHeight: '1.5'
          }}>
            Rasca el área plateada de tu tarjeta para revelar el código de 6 caracteres
          </p>
        </div>
        
        {/* Ilustración de tarjeta */}
        <div style={{
          backgroundColor: '#F9FAFB',
          border: '2px dashed #E5E7EB',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'inline-block',
            backgroundColor: '#D1D5DB',
            padding: '8px 24px',
            borderRadius: '4px',
            marginBottom: '8px'
          }}>
            <span style={{ 
              fontFamily: 'monospace', 
              fontSize: '18px',
              color: '#6B7280',
              letterSpacing: '4px'
            }}>
              ▒▒▒▒▒▒
            </span>
          </div>
          <p style={{ 
            fontSize: '12px', 
            color: '#9CA3AF',
            margin: 0
          }}>
            ↑ Rasca aquí para ver tu código
          </p>
        </div>
        
        {/* Inputs del código */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          marginBottom: '16px'
        }}>
          {codigo.map((char, index) => (
            <input
              key={index}
              ref={el => inputRefs.current[index] = el}
              type="text"
              maxLength={1}
              value={char}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              disabled={loading}
              style={{
                width: '50px',
                height: '60px',
                fontSize: '24px',
                fontWeight: '700',
                textAlign: 'center',
                border: error ? '2px solid #EF4444' : '2px solid #E5E7EB',
                borderRadius: '8px',
                outline: 'none',
                textTransform: 'uppercase',
                fontFamily: 'monospace',
                transition: 'all 0.2s',
                backgroundColor: loading ? '#F9FAFB' : 'white'
              }}
              onFocus={(e) => {
                if (!error) e.target.style.borderColor = '#FBBF24';
              }}
              onBlur={(e) => {
                if (!error) e.target.style.borderColor = '#E5E7EB';
              }}
            />
          ))}
        </div>
        
        {/* Contador de intentos */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '6px',
          marginBottom: '16px'
        }}>
          {[...Array(7)].map((_, index) => (
            <div
              key={index}
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: index < intentosRestantes ? '#FBBF24' : '#E5E7EB',
                transition: 'background-color 0.3s'
              }}
            />
          ))}
        </div>
        
        <p style={{ 
          textAlign: 'center', 
          fontSize: '13px', 
          color: '#9CA3AF',
          marginBottom: '20px'
        }}>
          {intentosRestantes} intento{intentosRestantes !== 1 ? 's' : ''} restante{intentosRestantes !== 1 ? 's' : ''}
        </p>
        
        {/* Error */}
        {error && (
          <div style={{
            backgroundColor: '#FEE2E2',
            border: '1px solid #FECACA',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <p style={{ 
              color: '#DC2626', 
              fontSize: '14px',
              margin: 0
            }}>
              {error}
            </p>
          </div>
        )}
        
        {/* Botón */}
        <button
          onClick={() => handleSubmit()}
          disabled={loading || codigo.join('').length !== 6}
          style={{
            width: '100%',
            padding: '14px',
            backgroundColor: loading || codigo.join('').length !== 6 ? '#E5E7EB' : '#FBBF24',
            color: loading || codigo.join('').length !== 6 ? '#9CA3AF' : '#1F2937',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: loading || codigo.join('').length !== 6 ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s'
          }}
        >
          {loading ? 'Validando...' : 'Verificar Código'}
        </button>
        
        {/* Ayuda */}
        <div style={{
          marginTop: '24px',
          paddingTop: '24px',
          borderTop: '1px solid #E5E7EB',
          textAlign: 'center'
        }}>
          <p style={{ fontSize: '13px', color: '#9CA3AF', marginBottom: '8px' }}>
            ¿No encuentras el código?
          </p>
          <a 
            href="/soporte" 
            style={{ 
              fontSize: '14px', 
              color: '#FBBF24',
              textDecoration: 'none',
              fontWeight: '500'
            }}
          >
            Contactar soporte
          </a>
        </div>
      </div>
    </div>
  );
};

export default CodigoActivacion;